import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  contentChild,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
  HostListener,
  forwardRef,
  ChangeDetectionStrategy,
  OnChanges,
  ChangeDetectorRef,
  SimpleChanges,
  Directive,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IconComponentV2 } from '../../icon-component/icon.component';
import { TooltipComponent } from '../tooltip/tooltip.component';

export interface DropdownOption {
  value: string;
  label: string;
  shortLabel?: string;
  description?: string;
  tooltip?: string;
  icon?: string;
  image?: string;
  disabled?: boolean;
  iconClass?: string;
  draggable?: boolean; // Allow per-option draggable control
  badge?: {
    text: string;
    variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'default';
  };
  badges?: {
    text: string;
    variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'default';
  }[];
  /** Renders as a sticky footer action button instead of a list item */
  isAction?: boolean;
}

export type DropdownVariant =
  | 'default'
  | 'outline'
  | 'ghost'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'themeBase';
export type DropdownSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type SelectionMode = 'single' | 'multiple';
export type SelectionStyle = 'check' | 'radio' | 'checkbox' | 'none';
export type DropdownPosition =
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right'
  | 'auto';
export type DropdownType = 'selection' | 'datepicker';

export interface DropdownTheme {
  // Container
  containerClass?: string;

  // Trigger
  triggerClass?: string;
  triggerVariants?: {
    default?: string;
    outline?: string;
    ghost?: string;
    primary?: string;
    success?: string;
    warning?: string;
    danger?: string;
    themeBase?: string;
  };
  triggerSizes?: {
    xs?: string;
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
  };

  // Menu
  menuClass?: string;
  menuWidth?: string;
  menuMaxHeight?: string;

  // Options
  optionClass?: string;
  optionHoverClass?: string;
  optionSelectedClass?: string;
  optionDisabledClass?: string;

  // Colors
  primaryColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;

  // Dark mode
  darkMode?: {
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    primaryColor?: string;
  };
}

export interface DatePickerValue {
  year?: number;
  month?: number;
}

/** Marks projected content as the dropdown trigger (replaces the default trigger button). */
@Directive({
  selector: '[appDropdownTrigger]',
  standalone: true,
})
export class DropdownTriggerDirective {}

/**
 * Dropdown Component with advanced features
 *
 * @example Basic Usage
 * <app-dropdown
 *   [options]="options"
 *   [(ngModel)]="selectedValue"
 *   placeholder="Select option">
 * </app-dropdown>
 *
 * @example With Drag and Drop
 * <app-dropdown
 *   [options]="options"
 *   [(ngModel)]="selectedValue"
 *   [draggable]="true"
 *   [showDragHandle]="true"
 *   (optionsReordered)="onOptionsReordered($event)">
 * </app-dropdown>
 *
 * Component Features:
 * - Single and multiple selection modes
 * - Searchable options
 * - Drag-and-drop reordering (conditional)
 * - Custom theming and styling
 * - DatePicker mode
 * - Apply button for batch changes
 * - Keyboard navigation
 * - Loading and disabled states
 */
@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [CommonModule, IconComponentV2, TooltipComponent],
  templateUrl: './dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
  styles: [
    `
      /* Drag and Drop Styles */
      .dragging {
        opacity: 0.5;
        cursor: move !important;
      }

      .drag-over {
        border-top: 2px solid rgb(168, 85, 247) !important;
        background-color: rgba(168, 85, 247, 0.05) !important;
      }

      .dark .drag-over {
        background-color: rgba(168, 85, 247, 0.15) !important;
      }

      /* Smooth drag transitions */
      button[draggable='true'] {
        transition: opacity 150ms ease, transform 150ms ease;
      }

      button[draggable='true']:active {
        cursor: grabbing !important;
      }

      /* Drag handle cursor */
      .cursor-move {
        cursor: move;
      }

      /* Simple Spinner */
      .spinner {
        width: 18px;
        height: 18px;
        border: 2px solid rgba(168, 85, 247, 0.2);
        border-top-color: #a855f7;
        border-radius: 50%;
        display: inline-block;
        animation: drop-spin 0.8s linear infinite;
      }

      @keyframes drop-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
  animations: [
    trigger('dropdownAnimation', [
      state(
        'closed',
        style({
          opacity: 0,
          marginTop: '-10px',
          visibility: 'hidden',
        })
      ),
      state(
        'open',
        style({
          opacity: 1,
          marginTop: '0px',
          visibility: 'visible',
        })
      ),
      transition('closed => open', [
        style({ visibility: 'visible' }),
        animate('200ms cubic-bezier(0.34, 1.56, 0.64, 1)'),
      ]),
      transition('open => closed', [
        animate('150ms cubic-bezier(0.4, 0.0, 1, 1)'),
        style({ visibility: 'hidden' }),
      ]),
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-5px)' }),
        animate(
          '150ms ease-out',
          style({ opacity: 1, transform: 'translateX(0)' })
        ),
      ]),
    ]),
  ],
})
export class DropdownComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @ViewChild('dropdownRef', { static: true }) dropdownRef!: ElementRef;
  @ViewChild('menuRef', { static: false }) menuRef?: ElementRef;

  private readonly customTrigger = contentChild(DropdownTriggerDirective);
  readonly hasCustomTrigger = computed(() => !!this.customTrigger());

  // Core Configuration
  @Input() options: DropdownOption[] = [];
  @Input() placeholder = 'Select option';
  @Input() searchPlaceholder = 'Search options...';
  @Input() emptyMessage = 'No options available';
  @Input() loadingMessage = 'Loading...';
  @Input() placeholderColor = '';

  @Input() selectedValue: any = '';
  @Input() isDelete?: boolean;

  // Selection Configuration
  @Input() tooltipMaxWidth: string = '320px';
  @Input() tooltipFixedStrategy: boolean = false;
  @Input() tooltipIsMarkdown: boolean = false;
  @Input() showTooltip: boolean = true;
  @Input() selectionMode: SelectionMode = 'single';
  @Input() selectionStyle: SelectionStyle = 'check';
  @Input() multiSelectDisplayMode: 'label' | 'count' = 'label';
  @Input() maxSelections?: number;
  @Input() clearable = false;
  @Input() searchable = false;
  @Input() loading = false;
  @Input() disabled = false;
  @Input() showApplyButton = false; // Show apply changes button
  @Input() applyButtonText = 'Apply'; // Text for apply button

  // Optional custom display formatter for the trigger text.
  // When provided (and selectionMode === 'multiple'), this overrides the default
  // comma-joined / count display logic so the parent can render a fully custom label.
  @Input() displayFormatter?: (
    selectedOptions: DropdownOption[],
    placeholder: string
  ) => string;

  // Remote Search Configuration
  @Input() remoteSearch = false; // If true, disables internal filtering
  @Input() debounceSearchTime = 300; // Time in ms to debounce search emission

  // Drag and Drop Configuration
  @Input() draggable = false; // Enable drag-and-drop reordering
  @Input() dragHandleIcon: string = 'between-horizontal-start'; // Icon for drag handle
  @Input() showDragHandle = true; // Show dedicated drag handle icon
  @Input() defaultValue: string | null = null;

  // Appearance Configuration
  @Input() variant: DropdownVariant = 'default';
  @Input() size: DropdownSize = 'md';
  @Input() position: DropdownPosition = 'auto';
  @Input() width?: string;
  @Input() maxHeight = '300px';
  @Input() theme?: DropdownTheme;
  @Input() customWidth?: string;
  @Input() customHeight?: string;
  @Input() fullWidth = false;
  @Input() rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  @Input() shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  @Input() borderWidth?: '0' | '1' | '2' | '4';
  @Input() wrapLabels = true;
  @Input() disableDarkTheme = false; // When true, strips all dark: classes (e.g. in widget/light contexts)
  // Title Configuration
  @Input() title?: string;
  @Input() titleClass?: string;
  @Input() menuTitle?: string;
  @Input() menuTitleClass?: string;
  @Input() required = false;
  @Input() error?: string;
  @Input() showErrorIndicator = true;
  @Input() hint?: string;

  // Type Configuration
  @Input() type: DropdownType = 'selection';

  // DatePicker Configuration (only used when type = 'datepicker')
  @Input() minYear = 1900;
  @Input() maxYear = new Date().getFullYear();
  @Input() minMonth?: number;
  @Input() maxMonth?: number;
  @Input() initialYear?: number;
  @Input() initialMonth?: number;
  @Input() iconName?: string; // support custom icons using app-icon-v2
  @Input() isApplyButtonShown?: boolean = false;
  @Input() triggerIconSize: number | string = 16;
  @Input() useImageForIcon: boolean = false;

  // Custom Classes
  @Input() triggerClass?: string;
  @Input() menuClass?: string;
  @Input() optionClass?: string;
  @Input() textClass?: string;
  @Input() className?: string;
  @Input() iconClass: string = '';
  /** When true the menu renders with position:fixed, positioned via JS coords from getBoundingClientRect.
   *  This lets it escape any overflow:hidden / stacking-context ancestors. */
  @Input() menuFixed: boolean = false;

  // Icons
  @Input() chevronIcon: string = 'chevron-right';
  @Input() clearIcon: string = 'x';
  @Input() searchIcon: string = 'search';
  @Input() checkIcon: string = 'check';
  @Input() closeButtonText: string = 'Close';

  @Input() isCheckUnCheck = false;

  // Events
  @Output() selectionChange = new EventEmitter<any>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() removeItem = new EventEmitter<DropdownOption>();
  @Output() openChange = new EventEmitter<boolean>();
  @Output() optionClick = new EventEmitter<DropdownOption>();
  @Output() optionsReordered = new EventEmitter<DropdownOption[]>(); // Emitted when options are reordered
  @Output() scrolledToEnd = new EventEmitter<void>(); // Emitted when user scrolls to bottom

  // Internal State
  isOpen = signal(false);
  searchQuery = signal('');
  selectedValues = signal<any[]>([]);
  pendingValues = signal<any[]>([]); // Temporary values when apply button is enabled
  focusedIndex = signal(-1);
  computedPosition = signal<DropdownPosition>('bottom-left');
  /** Fixed-position coords, populated when menuFixed=true and the menu opens */
  fixedMenuCoords = signal<{ top: number; left: number; width?: number } | null>(null);

  // Getters to sanitize custom dimensions (removes any stray quotes if passed like "'38px'")
  get cleanCustomHeight(): string | undefined {
    if (!this.customHeight) return undefined;
    return this.customHeight.replace(/['"]/g, '').trim();
  }

  get cleanCustomWidth(): string | undefined {
    if (!this.customWidth) return undefined;
    return this.customWidth.replace(/['"]/g, '').trim();
  }

  // Search Subject for debouncing
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private readonly cdr = inject(ChangeDetectorRef);

  // Internal options signal for drag-drop reordering and reactivity
  optionsSignal = signal<DropdownOption[]>([]);
  internalOptions = signal<DropdownOption[]>([]);

  // Drag and Drop State
  draggedIndex = signal<number | null>(null);
  dragOverIndex = signal<number | null>(null);
  isDragging = signal(false);

  // DatePicker State (only used when type = 'datepicker')
  selectedYear = signal<number | null>(null);
  selectedMonth = signal<number | null>(null);
  internalMinYear = signal<number>(1900);
  internalMaxYear = signal<number>(new Date().getFullYear());
  internalMinMonth = signal<number | null>(null);
  internalMaxMonth = signal<number | null>(null);

  // Computed Properties
  filteredOptions = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const options = this.optionsSignal();

    // If remote search is enabled, we don't filter internally
    if (this.remoteSearch || !query) return options;

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.description?.toLowerCase().includes(query)
    );
  });

  selectableOptions = computed(() =>
    this.filteredOptions().filter((option) => !option.isAction)
  );

  actionOptions = computed(() =>
    this.optionsSignal().filter((option) => option.isAction)
  );

  selectedOptions = computed(() => {
    const valuesToUse = this.selectedValues();
    return this.optionsSignal().filter((option) =>
      valuesToUse.includes(option.value)
    );
  });

  pendingOptions = computed(() => {
    return this.optionsSignal().filter((option) =>
      this.pendingValues().includes(option.value)
    );
  });

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      this.optionsSignal.set(this.options || []);
    }

    if (changes['selectedValue']) {
      if (this.selectedValue !== undefined && this.selectedValue !== null) {
        if (Array.isArray(this.selectedValue)) {
          // Multiple Selection Sync
          const searchValues = this.selectedValue;
          const validValues = this.optionsSignal()
            .filter(opt => searchValues.includes(opt.value))
            .map(opt => opt.value);
          this.selectedValues.set(validValues);
          // Keep pendingValues in sync so the apply-button menu reflects the
          // bound state instead of showing all checkboxes empty on first render.
          if (this.showApplyButton) {
            this.pendingValues.set([...validValues]);
          }
        } else {
        // Single Selection Sync
          const exists = this.optionsSignal().some(
            (opt) => opt.value === this.selectedValue
          );
          if (exists) {
            this.selectedValues.set([this.selectedValue]);
            if (this.showApplyButton) {
              this.pendingValues.set([this.selectedValue]);
            }
          } else {
            this.selectedValues.set([]);
            if (this.showApplyButton) {
              this.pendingValues.set([]);
            }
          }
        }
      } else {
        // Handle reset when selectedValue is null or undefined
        this.selectedValues.set([]);
        if (this.showApplyButton) {
          this.pendingValues.set([]);
        }
      }
    }

    // Sync internal options when input changes
    if (this.draggable && this.options) {
      this.internalOptions.set([...this.options]);
    }

    if (changes['defaultValue'] && this.defaultValue) {
      this.selectedValues.set([String(this.defaultValue)]);
    }

    if (this.type === 'datepicker') {
      if (changes['minYear']) {
        this.internalMinYear.set(this.minYear);
      }
      if (changes['maxYear']) {
        this.internalMaxYear.set(this.maxYear);
      }
      if (changes['minMonth']) {
        this.internalMinMonth.set(this.minMonth ?? null);
      }
      if (changes['maxMonth']) {
        this.internalMaxMonth.set(this.maxMonth ?? null);
      }

      // Check if current selection is still valid under new constraints
      const currentYear = this.selectedYear();
      const currentMonth = this.selectedMonth();

      if (currentYear !== null) {
        let isInvalid = false;

        // Check Year
        if (currentYear < this.minYear || currentYear > this.maxYear) {
          isInvalid = true;
        }

        // Check Month if year is minYear
        if (
          !isInvalid &&
          currentYear === this.minYear &&
          this.minMonth !== undefined &&
          currentMonth !== null &&
          currentMonth < this.minMonth
        ) {
          isInvalid = true;
        }

        // Check Month if year is maxYear
        if (
          !isInvalid &&
          currentYear === this.maxYear &&
          this.maxMonth !== undefined &&
          currentMonth !== null &&
          currentMonth > this.maxMonth
        ) {
          isInvalid = true;
        }

        if (isInvalid) {
          this.selectedYear.set(null);
          this.selectedMonth.set(null);
          this.emitDatePickerValue();
        }
      }
    }
  }

  deleteItem(item: DropdownOption) {
    this.removeItem.emit(item);
  }

  // displayText = computed(() => {
  //   // DatePicker display logic
  //   if (this.type === 'datepicker') {
  //     const year = this.selectedYear();
  //     const month = this.selectedMonth();

  //     if (year && month) {
  //       const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  //                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  //       return `${monthNames[month - 1]} ${year}`;
  //     }
  //     return this.placeholder;
  //   }

  //   // Selection dropdown display logic
  //   const selected = this.selectedOptions();
  //   if (selected.length === 0) return this.placeholder;

  //   if (this.selectionMode === 'single') {
  //     return selected[0]?.label || this.placeholder;
  //   }

  //   if (selected.length === 1) {
  //     return selected[0].label;
  //   }

  //   return `${selected?.map(opt => opt.label).join(', ')}`;
  //   // return `${selected.length} items selected`;
  // });

  hasChanges = computed(() => {
    if (!this.showApplyButton) return false;

    const current = [...this.selectedValues()].sort();
    const pending = [...this.pendingValues()].sort();

    // Disable if pending values are empty (nothing selected)
    if (pending.length === 0) return false;

    // Enable if values have changed
    return JSON.stringify(current) !== JSON.stringify(pending);
  });

  displayText = computed(() => {
    // DatePicker display logic
    if (this.type === 'datepicker') {
      // ... (DatePicker logic)
      const year = this.selectedYear();
      const month = this.selectedMonth();

      if (year && month) {
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        return `${monthNames[month - 1]} ${year}`;
      }
      if (year && !month) {
        return `${year}`;
      }
      return this.placeholder;
    }

    // Selection dropdown display logic - always show committed values
    const selected = this.selectedOptions();

    // Custom display formatter takes precedence for multiple selection mode.
    // Allows callers to render fully custom labels (e.g. "3 Months", "All months selected").
    if (this.selectionMode === 'multiple' && this.displayFormatter) {
      return this.displayFormatter(selected, this.placeholder);
    }

    // If nothing is selected, show the placeholder (e.g., "All Months")
    if (selected.length === 0) return this.placeholder;

    // Single Selection Mode
    if (this.selectionMode === 'single') {
      const first = selected[0];
      return first?.shortLabel || first?.label || this.placeholder;
    }

    // Multiple Selection Mode
    if (this.selectionMode === 'multiple' && !this.maxSelections) {
      const selected = this.selectedOptions();
      if (selected.length === 0) return this.placeholder;

      if (this.multiSelectDisplayMode === 'count') {
        return `${this.placeholder} (${selected.length})`;
      }

      return `${selected?.map((opt) => opt.label).join(', ')}`;
    }

    // Multiple Selection Mode (MODIFIED LOGIC)
    if (this.selectionMode === 'multiple' && this.maxSelections) {
      // This should be unreachable if selected.length > 0 is true, but kept as a fallback.
      return `${selected?.map((opt) => opt.label).join(', ')}`;
    }

    // Fallback

    return this.placeholder;
  });

  // DatePicker computed properties
  years = computed(() => {
    const yearsList = [];
    const min = this.internalMinYear();
    const max = this.internalMaxYear();
    for (let year = max; year >= min; year--) {
      yearsList.push(year);
    }
    return yearsList;
  });

  // months = computed(() => [
  //   { value: 1, label: 'January', short: 'Jan' },
  //   { value: 2, label: 'February', short: 'Feb' },
  //   { value: 3, label: 'March', short: 'Mar' },
  //   { value: 4, label: 'April', short: 'Apr' },
  //   { value: 5, label: 'May', short: 'May' },
  //   { value: 6, label: 'June', short: 'Jun' },
  //   { value: 7, label: 'July', short: 'Jul' },
  //   { value: 8, label: 'August', short: 'Aug' },
  //   { value: 9, label: 'September', short: 'Sep' },
  //   { value: 10, label: 'October', short: 'Oct' },
  //   { value: 11, label: 'November', short: 'Nov' },
  //   { value: 12, label: 'December', short: 'Dec' },
  // ]);

  // months = computed(() => {
  //   const selectedYear = this.selectedYear();
  //   const minYear = this.minYear;
  //   const minMonth = this.minMonth;

  //   const allMonths = [
  //     { value: 1, label: 'January', short: 'Jan' },
  //     { value: 2, label: 'February', short: 'Feb' },
  //     { value: 3, label: 'March', short: 'Mar' },
  //     { value: 4, label: 'April', short: 'Apr' },
  //     { value: 5, label: 'May', short: 'May' },
  //     { value: 6, label: 'June', short: 'Jun' },
  //     { value: 7, label: 'July', short: 'Jul' },
  //     { value: 8, label: 'August', short: 'Aug' },
  //     { value: 9, label: 'September', short: 'Sep' },
  //     { value: 10, label: 'October', short: 'Oct' },
  //     { value: 11, label: 'November', short: 'Nov' },
  //     { value: 12, label: 'December', short: 'Dec' },
  //   ];

  //   // 🔥 Key logic
  //   if (selectedYear && minMonth && selectedYear === minYear) {
  //     return allMonths.filter((m) => m.value >= minMonth);
  //   }

  //   return allMonths;
  // });

  months = computed(() => {
    const selectedYear = this.selectedYear();
    const minYear = this.internalMinYear();
    const minMonth = this.internalMinMonth();
    const maxYear = this.internalMaxYear();
    const maxMonth = this.internalMaxMonth();

    const allMonths = [
      { value: 1, label: 'January', short: 'Jan' },
      { value: 2, label: 'February', short: 'Feb' },
      { value: 3, label: 'March', short: 'Mar' },
      { value: 4, label: 'April', short: 'Apr' },
      { value: 5, label: 'May', short: 'May' },
      { value: 6, label: 'June', short: 'Jun' },
      { value: 7, label: 'July', short: 'Jul' },
      { value: 8, label: 'August', short: 'Aug' },
      { value: 9, label: 'September', short: 'Sep' },
      { value: 10, label: 'October', short: 'Oct' },
      { value: 11, label: 'November', short: 'Nov' },
      { value: 12, label: 'December', short: 'Dec' },
    ];

    let filtered = [...allMonths];

    // 🔥 Apply minMonth ONLY for the same year
    if (selectedYear != null && minMonth != null && selectedYear === minYear) {
      filtered = filtered.filter((m) => m.value >= minMonth);
    }

    // 🔥 Apply maxMonth ONLY for the same year
    if (selectedYear != null && maxMonth != null && selectedYear === maxYear) {
      filtered = filtered.filter((m) => m.value <= maxMonth);
    }

    return filtered;
  });

  // Strips all `dark:*` Tailwind utility classes from a class string
  public stripDarkClasses(classes: string): string {
    return classes
      .split(' ')
      .filter(c => !c.startsWith('dark:'))
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  // Returns classes based on the disableDarkTheme setttings
  public applyThemeClasses(classes: string | undefined): string {
    if (!classes) return '';
    if (!this.disableDarkTheme) return classes;
    return this.stripDarkClasses(classes);
  }

  // Tailwind-based trigger classes
  getDropdownTriggerClasses(): string {
    const baseClasses =
      'flex items-center cursor-pointer transition-all duration-150 text-sm outline-none relative';

    // Size classes
    const sizeClasses =
      this.theme?.triggerSizes?.[this.size] || this.getDefaultSizeClasses();

    // Variant classes
    const variantClasses =
      this.theme?.triggerVariants?.[this.variant] ||
      this.getDefaultVariantClasses();

    // Width classes
    const widthClasses = this.fullWidth
      ? 'w-full'
      : this.customWidth
        ? `w-[${this.customWidth}]`
        : 'w-full';

    // Border radius
    const roundedClasses = this.getRoundedClasses();

    // Shadow classes
    const shadowClasses = this.getShadowClasses();

    // Border width
    const borderWidthClass = this.borderWidth
      ? `border-${this.borderWidth}`
      : 'border';

    // Custom classes
    const customClasses = this.theme?.triggerClass || this.triggerClass || '';

    // Disabled state
    const disabledClasses = this.disabled
      ? 'disabled:opacity-50 disabled:cursor-not-allowed'
      : '';

    // Error state
    const errorClasses = this.error ? 'border-red-500 dark:border-red-400' : '';

    let finalClasses = [
      baseClasses,
      sizeClasses,
      variantClasses,
      widthClasses,
      roundedClasses,
      shadowClasses,
      borderWidthClass,
      disabledClasses,
      errorClasses,
      customClasses,
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');

    if (this.disableDarkTheme) finalClasses = this.stripDarkClasses(finalClasses);
    return finalClasses;
  }

  getDropdownMenuClasses(): string {
    const posType = this.menuFixed ? 'fixed' : 'absolute';
    const baseClasses = `${posType} z-[9999] border overflow-y-auto overflow-x-hidden overscroll-y-contain`;

    // Background and text colors
    const colorClasses =
      this.theme?.backgroundColor || 'bg-white dark:bg-gray-800';
    const borderClasses =
      this.theme?.borderColor || 'border-gray-200 dark:border-gray-600';

    // Size classes
    const maxHeightClass =
      this.theme?.menuMaxHeight || `max-h-[${this.maxHeight}]`;
    // Fixed menus get an explicit pixel width from the trigger rect; `min-w-full` would
    // resolve against the viewport and blow out the panel (unpaid-payments filter bars).
    const widthClass = this.menuFixed
      ? 'w-full min-w-0 max-w-full box-border'
      : this.theme?.menuWidth ||
        (this.cleanCustomWidth ? `w-[${this.cleanCustomWidth}]` : 'w-full min-w-full');

    // Position classes
    const position = this.computedPosition();
    let positionClasses = '';
    
    if (position.includes('top')) {
      positionClasses = 'bottom-full mb-1';
      if (position.includes('right')) {
        positionClasses += ' right-0';
      } else {
        positionClasses += ' left-0';
      }
    } else {
      positionClasses = 'top-full mt-1';
      if (position.includes('right')) {
        positionClasses += ' right-0';
      } else {
        positionClasses += ' left-0';
      }
    }

    // Border radius and shadow
    const roundedClasses = this.getRoundedClasses();
    const shadowClasses = this.getShadowClasses() || 'shadow-lg';

    // Custom scrollbar classes using Tailwind
    const scrollbarClasses = `
      scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 
      dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800
      [&::-webkit-scrollbar]:w-1.5
      [&::-webkit-scrollbar-track]:bg-gray-100 
      [&::-webkit-scrollbar-track]:dark:bg-gray-800
      [&::-webkit-scrollbar-thumb]:bg-gray-300 
      [&::-webkit-scrollbar-thumb]:dark:bg-gray-600 
      [&::-webkit-scrollbar-thumb]:rounded-full
      [&::-webkit-scrollbar-thumb:hover]:bg-gray-400 
      [&::-webkit-scrollbar-thumb:hover]:dark:bg-gray-500
    `
      .replace(/\s+/g, ' ')
      .trim();

    // Custom classes
    const customClasses = this.theme?.menuClass || this.menuClass || '';

    let menuClasses = [
      baseClasses,
      colorClasses,
      borderClasses,
      maxHeightClass,
      widthClass,
      positionClasses,
      roundedClasses,
      shadowClasses,
      scrollbarClasses,
      customClasses,
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');

    if (this.disableDarkTheme) menuClasses = this.stripDarkClasses(menuClasses);
    return menuClasses;
  }

  getOptionClasses(option: DropdownOption, index: number): string {
    const baseClasses =
      'flex items-center cursor-pointer transition-colors duration-150';

    // Size-based padding
    const paddingClasses = this.getOptionPaddingClasses();

    // Custom option classes
    const customClasses = this.theme?.optionClass || this.optionClass || '';

    let result: string;

    if (option.disabled) {
      const disabledClasses =
        this.theme?.optionDisabledClass ||
        'opacity-50 cursor-not-allowed text-gray-400 dark:text-gray-500';
      result = [baseClasses, paddingClasses, disabledClasses, customClasses]
        .filter(Boolean)
        .join(' ')
        .trim()
        .replace(/\s+/g, ' ');
    } else if (this.isSelected(option)) {
      const selectedClasses =
        this.theme?.optionSelectedClass ||
        'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      result = [baseClasses, paddingClasses, selectedClasses, customClasses]
        .filter(Boolean)
        .join(' ')
        .trim()
        .replace(/\s+/g, ' ');
    } else if (this.focusedIndex() === index) {
      const focusedClasses =
        'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white';
      result = [baseClasses, paddingClasses, focusedClasses, customClasses]
        .filter(Boolean)
        .join(' ')
        .trim()
        .replace(/\s+/g, ' ');
    } else {
      // Default option styling
      const textClasses =
        this.theme?.textColor || 'text-gray-900 dark:text-gray-100';
      const hoverClasses =
        this.theme?.optionHoverClass ||
        'hover:bg-gray-100 dark:hover:bg-gray-700';
      result = [
        baseClasses,
        paddingClasses,
        textClasses,
        hoverClasses,
        customClasses,
      ]
        .filter(Boolean)
        .join(' ')
        .trim()
        .replace(/\s+/g, ' ');
    }

    if (this.disableDarkTheme) result = this.stripDarkClasses(result);
    return result;
  }

  // ControlValueAccessor
  private onChange = (value: any) => { };
  onTouched = () => { };

  /**
   * Smart blur handler for the trigger button.
   *
   * WHY this exists instead of a plain (blur)="onTouched()":
   * When the dropdown is OPEN and the user clicks an option, the browser fires
   * `blur` on the trigger button BEFORE the option's `click` event resolves.
   * Calling onTouched() at that point marks the control as touched while its
   * value is still empty, causing a spurious "required" validation flash.
   *
   * When the dropdown is CLOSED, blur is a genuine focus-leave (e.g. Tab key),
   * and we DO want to mark the control as touched for proper form UX.
   *
   * When the dropdown IS open, we skip it here — close() will call onTouched()
   * after onChange() has already committed the selected value.
   */
  onBlur(): void {
    if (!this.isOpen()) {
      this.onTouched();
    }
  }

  ngOnInit(): void {
    // Set initial options signal
    this.optionsSignal.set(this.options || []);

    // Initialize default selected value
    if (this.type !== 'datepicker' && this.defaultValue) {
      this.selectedValues.set([String(this.defaultValue)]);
    }
    // Initialize datepicker values if provided
    if (this.type === 'datepicker') {
      if (this.initialYear) {
        this.selectedYear.set(this.initialYear);
      }
      if (this.initialMonth) {
        this.selectedMonth.set(this.initialMonth);
      }
    }

    // Setup debounced search emission
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(this.debounceSearchTime),
        distinctUntilChanged()
      )
      .subscribe((query) => {
        this.searchChange.emit(query);
      });

    this.updatePosition();
  }

  // DatePicker Methods
  selectYear(year: number): void {
    this.selectedYear.set(year);

    // 🚨 Reset month ONLY if invalid for minYear
    if (
      this.minMonth &&
      year === this.minYear &&
      this.selectedMonth() &&
      this.selectedMonth()! < this.minMonth
    ) {
      this.selectedMonth.set(null);
    }

    this.emitDatePickerValue();
  }

  selectMonth(month: number): void {
    this.selectedMonth.set(month);
    this.emitDatePickerValue();
  }

  applyYearMonthChanges() {
    const year = this.selectedYear();
    const month = this.selectedMonth();

    if (year && month) {
      const dateValue: DatePickerValue = { year, month };
      this.selectionChange.emit(dateValue);
      this.onChange(dateValue);
    }
    if (year && !month) {
      const dateValue: DatePickerValue = { year };
      this.selectionChange.emit(dateValue);
      this.onChange(dateValue);
    }
  }

  private emitDatePickerValue(): void {
    const year = this.selectedYear();
    const month = this.selectedMonth();

    if (year && month) {
      const dateValue: DatePickerValue = { year, month };
      this.selectionChange.emit(dateValue);
      this.onChange(dateValue);
    }
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.searchSubject.complete();
  }

  // ControlValueAccessor Implementation
  writeValue(value: any): void {
    if (value === null || value === undefined) {
      if (this.type === 'datepicker') {
        this.selectedYear.set(null);
        this.selectedMonth.set(null);
      } else {
        this.selectedValues.set([]);
        if (this.showApplyButton) {
          this.pendingValues.set([]);
        }
      }
      return;
    }

    if (
      this.type === 'datepicker' &&
      typeof value === 'object' &&
      'year' in value &&
      'month' in value
    ) {
      this.selectedYear.set(value.year);
      this.selectedMonth.set(value.month);
    } else {
      const values = Array.isArray(value) ? value : [value];
      this.selectedValues.set(values);
      // Initialize pending values when apply button is shown
      if (this.showApplyButton) {
        this.pendingValues.set([...values]);
      }
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // Public Methods
  toggle(): void {
    if (this.disabled) return;

    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  onCustomTriggerClick(event: Event): void {
    if (this.disabled) return;
    event.stopPropagation();
    this.toggle();
  }

  onCustomTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  }

  open(): void {
    if (this.disabled) return;

    // Sync pending values with selected values when opening (if apply button is enabled)
    if (this.showApplyButton) {
      this.pendingValues.set([...this.selectedValues()]);
    }

    this.isOpen.set(true);
    this.updatePosition();

    // Calculate fixed coords when menuFixed=true
    if (this.menuFixed) {
      const rect = this.dropdownRef.nativeElement.getBoundingClientRect();
      this.fixedMenuCoords.set({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }

    this.openChange.emit(true);
    this.focusedIndex.set(-1);

    // Focus search input if searchable
    if (this.searchable) {
      setTimeout(() => {
        const searchInput = this.menuRef?.nativeElement.querySelector(
          '.dropdown-search input'
        );
        searchInput?.focus();
      }, 0);
    }
  }

  close(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.searchQuery.set('');
    this.openChange.emit(false);
    this.onTouched();
  }

  // selectOption(option: DropdownOption): void {
  //   if (option.disabled) return;

  //   const currentValues = this.selectedValues();
  //   let newValues: any[];

  //   if (this.selectionMode === 'single') {
  //     newValues = [option.value];
  //     this.close();
  //   } else {
  //     if (currentValues.includes(option.value)) {
  //       newValues = currentValues.filter(v => v !== option.value);
  //     } else {
  //       if (this.maxSelections && currentValues.length >= this.maxSelections) {
  //         return; // Max selections reached
  //       }
  //       newValues = [...currentValues, option.value];
  //     }
  //   }

  //   this.selectedValues.set(newValues);

  //   const emitValue = this.selectionMode === 'single'
  //     ? (newValues[0] || null)
  //     : newValues;

  //   this.onChange(emitValue);
  //   this.selectionChange.emit(emitValue);
  //   this.optionClick.emit(option);
  // }

  selectOption(option: DropdownOption): void {
    if (option.disabled) return;

    // Use pending values when apply button is shown, otherwise use selected values
    const currentValues = this.showApplyButton
      ? this.pendingValues()
      : this.selectedValues();
    let newValues: any[];

    // --- START: LOGIC FOR 'All' option ---
    if (option.value === 'all') {
      // Get all option values except 'all' itself and disabled options
      const opts = this.draggable ? this.internalOptions() : this.options;
      const allMonthValues = opts
        .filter((o) => o.value !== 'all' && !o.disabled)
        .map((o) => o.value);

      // Check if all options are currently selected
      const allMonthsSelected = allMonthValues.every((val) =>
        currentValues.includes(val)
      );

      if (allMonthsSelected) {
        // Case 1: All options are currently selected -> UNSELECT ALL
        newValues = [];
      } else {
        // Case 2: Not all options are selected (or none are) -> SELECT ALL
        // Ensure 'all' is not included in the final list, only the option values
        newValues = allMonthValues;
      }
    } else if (this.selectionMode === 'single') {
      // --- END: LOGIC FOR 'All' option ---

      // Existing single selection logic
      if (this.isCheckUnCheck && currentValues.includes(option.value)) {
        newValues = [];
      } else {
        newValues = [option.value];
      }
      // NOTE: close() is intentionally NOT called here.
      // It is deferred to after onChange() so that onTouched()
      // fires only after the form control already holds the new value,
      // preventing a spurious 'required' validation error on first selection.
    } else {
      // Existing multiple selection logic for individual options
      if (currentValues.includes(option.value)) {
        // Unselecting an individual option
        newValues = currentValues.filter((v) => v !== option.value);
      } else {
        // Selecting an individual option
        if (this.maxSelections && currentValues.length >= this.maxSelections) {
          return; // Max selections reached
        }
        newValues = [...currentValues, option.value];
      }
    }

    if (this.showApplyButton) {
      // Store in pending values when apply button is enabled
      this.pendingValues.set(newValues);
      this.optionClick.emit(option);
    } else {
      // Emit immediately when apply button is not enabled
      this.selectedValues.set(newValues);

      const emitValue =
        this.selectionMode === 'single' ? newValues[0] ?? null : newValues;

      this.onChange(emitValue);
      this.selectionChange.emit(emitValue);
      this.optionClick.emit(option);

      // Close AFTER onChange so that onTouched() (called inside close())
      // fires only once the form control already holds the new valid value.
      if (this.selectionMode === 'single' && !this.isCheckUnCheck) {
        this.close();
      }
    }
  }

  clearSelection(): void {
    if (this.type === 'datepicker') {
      this.selectedYear.set(null);
      this.selectedMonth.set(null);
      this.selectionChange.emit(null);
      this.onChange(null);
    } else {
      if (this.showApplyButton) {
        this.pendingValues.set([]);
      } else {
        this.selectedValues.set([]);
        this.onChange(this.selectionMode === 'single' ? null : []);
        this.selectionChange.emit(this.selectionMode === 'single' ? null : []);
      }
    }
  }

  isSelected(option: DropdownOption): boolean {
    // When apply button is shown, check pending values for UI state
    const valuesToCheck = this.showApplyButton
      ? this.pendingValues()
      : this.selectedValues();

    // Special handling for 'all' option - it's selected when all other options are selected
    if (option.value === 'all') {
      const opts = this.draggable ? this.internalOptions() : this.options;
      const allOptionValues = opts
        .filter((o) => o.value !== 'all' && !o.disabled)
        .map((o) => o.value);
      return (
        allOptionValues.length > 0 &&
        allOptionValues.every((val) => valuesToCheck.includes(val))
      );
    }

    return valuesToCheck.includes(option.value);
  }

  applyChanges(): void {
    if (!this.showApplyButton) return;

    const newValues = this.pendingValues();
    this.selectedValues.set(newValues);

    const emitValue =
      this.selectionMode === 'single' ? newValues[0] ?? null : newValues;

    this.onChange(emitValue);
    this.selectionChange.emit(emitValue);
    this.close();
  }

  cancelChanges(): void {
    if (!this.showApplyButton) return;

    // Reset pending values to current selected values
    this.pendingValues.set([...this.selectedValues()]);
    this.close();
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);

    if (this.remoteSearch) {
      // Push to subject for debounced emission
      this.searchSubject.next(input.value);
    } else {
      // Immediate emission for local search
      this.searchChange.emit(input.value);
    }

    this.focusedIndex.set(-1);
  }

  onScroll(event: Event): void {
    if (this.type !== 'selection' || this.loading) return;

    const element = event.target as HTMLElement;
    const threshold = 20; // 20px from bottom
    const isAtBottom =
      element.scrollHeight - element.scrollTop <=
      element.clientHeight + threshold;

    if (isAtBottom) {
      this.scrolledToEnd.emit();
    }
  }

  // Keyboard Navigation
  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen()) {
      if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'ArrowDown'
      ) {
        event.preventDefault();
        this.open();
      }
      return;
    }

    const filteredOpts = this.filteredOptions();
    const currentFocus = this.focusedIndex();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex =
          currentFocus < filteredOpts.length - 1 ? currentFocus + 1 : 0;
        this.focusedIndex.set(nextIndex);
        this.scrollToOption(nextIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        const prevIndex =
          currentFocus > 0 ? currentFocus - 1 : filteredOpts.length - 1;
        this.focusedIndex.set(prevIndex);
        this.scrollToOption(prevIndex);
        break;

      case 'Enter':
        event.preventDefault();
        if (currentFocus >= 0 && filteredOpts[currentFocus]) {
          this.selectOption(filteredOpts[currentFocus]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  }

  // Click Outside Handler
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event): void {
    if (!this.isOpen()) return;
    if (!this.dropdownRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  private scrollToOption(index: number): void {
    setTimeout(() => {
      const optionElement = this.menuRef?.nativeElement.querySelector(
        `[data-option-index="${index}"]`
      );
      optionElement?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  private updatePosition(): void {
    if (this.position !== 'auto') {
      this.computedPosition.set(this.position);
      return;
    }

    // Auto-detect best position based on viewport
    setTimeout(() => {
      if (!this.dropdownRef) return;

      const rect = this.dropdownRef.nativeElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = viewportWidth - rect.left;

      let position: DropdownPosition = 'bottom-left';

      // Use 300 as threshold since default maxHeight is 300px
      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        position = spaceRight < 300 ? 'top-right' : 'top-left';
      } else {
        position = spaceRight < 300 ? 'bottom-right' : 'bottom-left';
      }

      this.computedPosition.set(position);
    }, 0);
  }

  // Private helper methods for styling
  private getDefaultSizeClasses(): string {
    const sizeMap = {
      xs: 'min-h-[1.75rem] px-2 py-1 text-xs',
      sm: 'min-h-[2rem] px-2.5 py-1.5 text-sm',
      md: 'min-h-[2.5rem] px-3 py-2.5 text-sm',
      lg: 'min-h-[3rem] px-4 py-3 text-base',
      xl: 'min-h-[3.5rem] px-5 py-4 text-lg',
    };
    return sizeMap[this.size];
  }

  getSearchContainerClasses(): string {
    let classes = 'p-3 border-b border-gray-200 dark:border-gray-600';
    if (this.disableDarkTheme) classes = this.stripDarkClasses(classes);
    return classes;
  }

  getSearchInputClasses(): string {
    let classes = 'w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 dark:focus:border-purple-400';
    if (this.disableDarkTheme) classes = this.stripDarkClasses(classes);
    return classes;
  }

  private getDefaultVariantClasses(): string {
    const variantMap = {
      default:
        'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 dark:focus:border-purple-400 hover:border-gray-400 dark:hover:border-gray-500',
      outline:
        'border-2 border-purple-300 dark:border-purple-600 bg-transparent text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 dark:focus:border-purple-400 hover:border-purple-400 dark:hover:border-line-strong',
      ghost:
        'border-transparent bg-transparent text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500/20 focus:bg-purple-50 dark:focus:bg-purple-900/20 hover:bg-purple-50 dark:hover:bg-white/5',
      primary:
        'border-purple-500 dark:border-purple-400 bg-purple-500 dark:bg-purple-600 text-white focus:ring-2 focus:ring-purple-500/50 hover:bg-purple-600 dark:hover:bg-purple-700',
      success:
        'border-green-500 dark:border-green-400 bg-green-500 dark:bg-green-600 text-white focus:ring-2 focus:ring-green-500/50 hover:bg-green-600 dark:hover:bg-green-700',
      warning:
        'border-amber-500 dark:border-amber-400 bg-amber-500 dark:bg-amber-600 text-white focus:ring-2 focus:ring-amber-500/50 hover:bg-amber-600 dark:hover:bg-amber-700',
      danger:
        'border-red-500 dark:border-red-400 bg-red-500 dark:bg-red-600 text-white focus:ring-2 focus:ring-red-500/50 hover:bg-red-600 dark:hover:bg-red-700',
      themeBase:
        'border-[#EAEAEA] dark:border-line bg-[#FFFFFF] dark:bg-surface-1 text-[#141437] dark:text-white hover:border-purple-400 dark:hover:border-line-strong hover:bg-white dark:hover:bg-surface-1/50 hover:shadow-lg transition-all duration-300',
    };
    return variantMap[this.variant];
  }

  private getOptionPaddingClasses(): string {
    const sizeMap = {
      xs: 'px-2 py-1',
      sm: 'px-2.5 py-1.5',
      md: 'px-4 py-2.5',
      lg: 'px-5 py-3',
      xl: 'px-6 py-4',
    };
    return sizeMap[this.size];
  }

  private getRoundedClasses(): string {
    if (!this.rounded) return 'rounded-lg';

    const roundedMap = {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      full: 'rounded-full',
    };
    return roundedMap[this.rounded];
  }

  private getShadowClasses(): string {
    if (!this.shadow) return '';

    const shadowMap = {
      none: 'shadow-none',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
    };
    return shadowMap[this.shadow];
  }

  getBadgeClasses(variant: string = 'default'): string {
    const sizeClasses =
      this.size === 'xs'
        ? 'text-xs px-1.5 py-0.5'
        : this.size === 'sm'
          ? 'text-xs px-2 py-1'
          : this.size === 'lg'
            ? 'text-sm px-2.5 py-1'
            : this.size === 'xl'
              ? 'text-base px-3 py-1.5'
              : 'text-xs px-2 py-1';

    const variants = {
      default: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
      primary:
        'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200',
      secondary:
        'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
      success:
        'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
      warning:
        'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
      danger: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
    };

    let finalClasses = `inline-flex items-center rounded-md font-medium ${sizeClasses} ${variants[variant as keyof typeof variants] || variants.default}`;
    if (this.disableDarkTheme) finalClasses = this.stripDarkClasses(finalClasses);
    return finalClasses;
  }

  getSelectionIcon(): string {
    if (this.selectionStyle === 'radio') return 'circle-alert';
    if (this.selectionStyle === 'checkbox') return 'square-pen';
    return this.checkIcon;
  }

  trackOption(index: number, option: DropdownOption): any {
    return option.value ?? index;
  }

  // Drag and Drop Methods
  onDragStart(event: DragEvent, index: number, option: DropdownOption): void {
    if (!this.draggable || option.draggable === false) return;

    this.draggedIndex.set(index);
    this.isDragging.set(true);

    // Set drag data
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData(
        'text/html',
        (event.target as HTMLElement).innerHTML
      );
    }

    // Add dragging class to element
    (event.target as HTMLElement).classList.add('dragging');
  }

  onDragOver(event: DragEvent, index: number, option: DropdownOption): void {
    if (!this.draggable || option.draggable === false) return;

    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    const draggedIdx = this.draggedIndex();
    if (draggedIdx !== null && draggedIdx !== index) {
      this.dragOverIndex.set(index);
    }
  }

  onDragEnter(event: DragEvent, index: number, option: DropdownOption): void {
    if (!this.draggable || option.draggable === false) return;

    const draggedIdx = this.draggedIndex();
    if (draggedIdx !== null && draggedIdx !== index) {
      (event.currentTarget as HTMLElement).classList.add('drag-over');
    }
  }

  onDragLeave(event: DragEvent): void {
    if (!this.draggable) return;

    (event.currentTarget as HTMLElement).classList.remove('drag-over');
  }

  onDrop(event: DragEvent, dropIndex: number, option: DropdownOption): void {
    if (!this.draggable || option.draggable === false) return;

    event.preventDefault();
    event.stopPropagation();

    const draggedIdx = this.draggedIndex();
    if (draggedIdx === null || draggedIdx === dropIndex) {
      this.resetDragState();
      return;
    }

    // Reorder the internal options array
    const newOptions = [...this.internalOptions()];
    const draggedItem = newOptions[draggedIdx];

    // Remove from old position
    newOptions.splice(draggedIdx, 1);

    // Insert at new position
    newOptions.splice(dropIndex, 0, draggedItem);

    // Update the internal options signal
    this.internalOptions.set(newOptions);

    // Emit the event for parent component to handle if needed
    this.optionsReordered.emit(newOptions);

    this.resetDragState();
  }

  onDragEnd(event: DragEvent): void {
    if (!this.draggable) return;

    (event.target as HTMLElement).classList.remove('dragging');
    this.resetDragState();
  }

  private resetDragState(): void {
    this.draggedIndex.set(null);
    this.dragOverIndex.set(null);
    this.isDragging.set(false);

    // Remove all drag-over classes
    if (this.menuRef) {
      const elements =
        this.menuRef.nativeElement.querySelectorAll('.drag-over');
      elements.forEach((el: Element) => el.classList.remove('drag-over'));
    }
  }

  getDragClasses(index: number): string {
    const classes = [];

    if (this.draggedIndex() === index) {
      classes.push('opacity-50');
    }

    if (this.dragOverIndex() === index && this.draggedIndex() !== index) {
      classes.push('border-t-2 border-purple-500');
    }

    return classes.join(' ');
  }
}
